import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CalendarClock,
  Check,
  ChevronRight,
  FileText,
  MapPin,
  MessageCircle,
  Play,
  Users,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import VideoModal from "../components/VideoModal";
import { BALI, PROJECTS, VIDEOS, GET_STARTED, IMG } from "../mock";
import { Icon, Reveal, Eyebrow, CircleMark, SectionHeading } from "../components/common";
import { useParallax } from "../lib/useReveal";
import { BaliGallery, BaliFaqs, BaliTours } from "../components/bali/BaliExtras";

const CATEGORIES = ["All projects", "Wildlife & Conservation", "Teaching & Childcare", "Healthcare & Nutrition", "Community & Construction"];

export default function Bali() {
  const [video, setVideo] = useState(false);
  const [cat, setCat] = useState("All projects");
  const offset = useParallax(0.2);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const baliProjects = useMemo(
    () => PROJECTS.filter((p) => p.region === "Asia" || p.interest === cat),
    [cat]
  );

  const shown = useMemo(
    () =>
      cat === "All projects"
        ? PROJECTS.filter((p) => p.region === "Asia")
        : PROJECTS.filter((p) => p.interest === cat),
    [cat]
  );

  return (
    <div className="bg-white">
      <Navbar />

      {/* HERO */}
      <section className="relative isolate min-h-[86svh] overflow-hidden bg-ivi-slate">
        <div
          className="absolute inset-0 -z-10"
          style={{ transform: `translate3d(0, ${offset}px, 0) scale(1.1)` }}
        >
          <img src={BALI.hero} alt="Turquoise water in Bali" className="h-full w-full object-cover" />
        </div>
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-ivi-slate/80 via-ivi-slate/40 to-ivi-slate/85" />
        <div className="grain absolute inset-0 -z-10" />

        <div className="mx-auto flex min-h-[86svh] max-w-[1400px] flex-col justify-end px-5 pb-28 pt-36 lg:px-8">
          <nav className="flex items-center gap-2 text-[13px] font-semibold text-white/70">
            <Link to="/" className="transition-colors duration-200 hover:text-white">Home</Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <span>Destinations</span>
            <ChevronRight className="h-3.5 w-3.5" />
            <span className="text-ivi-lime">Bali</span>
          </nav>

          <h1 className="mt-6 font-heading text-[62px] font-extrabold leading-[0.95] tracking-[-0.03em] text-white sm:text-[96px] lg:text-[120px]">
            Bali
          </h1>
          <p className="mt-3 font-heading text-[15px] font-semibold uppercase tracking-[0.22em] text-ivi-lime">
            {BALI.kicker}
          </p>

          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href="#bali-projects"
              className="group inline-flex items-center gap-2 rounded-full bg-ivi-green px-7 py-4 font-heading text-[15px] font-bold text-white transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-ivi-greenDark"
            >
              See 13 Bali projects
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </a>
            <button
              type="button"
              onClick={() => setVideo(true)}
              className="group inline-flex items-center gap-3 rounded-full border border-white/30 bg-white/10 px-6 py-3 font-heading text-[15px] font-bold text-white backdrop-blur-md transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-white/20"
            >
              <span className="grid h-9 w-9 place-items-center rounded-full bg-white text-ivi-greenDark transition-transform duration-200 group-hover:scale-110">
                <Play className="ml-0.5 h-4 w-4 fill-current" />
              </span>
              Watch the film
            </button>
          </div>
        </div>

      </section>

      {/* Quick stats: outside the hero so nothing is clipped */}
      <div className="relative z-30 -mt-[62px] px-5 lg:px-8">
        <div className="mx-auto grid max-w-[1000px] grid-cols-2 gap-px overflow-hidden rounded-3xl bg-white shadow-[0_30px_70px_-30px_rgba(24,40,32,0.45)] ring-1 ring-ivi-mintDeep md:grid-cols-4">
          {BALI.quickStats.map((s) => (
            <div key={s.label} className="bg-white px-6 py-7 text-center">
              <p className="font-heading text-[26px] font-extrabold leading-none text-ivi-greenDark">
                {s.value}
              </p>
              <p className="mt-2 text-[12px] font-semibold uppercase tracking-[0.16em] text-ivi-slateSoft">
                {s.label}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* WHY WE NEED YOU */}
      <section className="bg-white pb-24 pt-28 lg:pb-28 lg:pt-36">
        <div className="mx-auto grid max-w-[1400px] items-center gap-16 px-5 lg:grid-cols-2 lg:px-8">
          <div>
            <h2 className="font-heading text-[34px] font-extrabold leading-[1.15] tracking-[-0.02em] text-ivi-slate sm:text-[44px]">
              <CircleMark>WHY</CircleMark> we need you in Bali
            </h2>
            <ul className="mt-10 space-y-5">
              {BALI.whyNeeded.map((w, i) => (
                <Reveal key={w.text} delay={i * 80}>
                  <li className="flex gap-4 rounded-2xl border border-ivi-mintDeep bg-ivi-sand p-5">
                    <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-white text-ivi-greenDark">
                      <Icon name={w.icon} className="h-5 w-5" />
                    </span>
                    <p className="text-[16px] leading-relaxed text-ivi-slate">{w.text}</p>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>

          <Reveal delay={120}>
            <div className="relative">
              <div className="overflow-hidden rounded-[28px] shadow-[0_30px_70px_-34px_rgba(24,40,32,0.5)]">
                <img
                  src={IMG.baliTeaching}
                  alt="Volunteer teaching children in Bali"
                  className="aspect-[5/4] w-full object-cover"
                />
              </div>
              <div className="absolute -bottom-7 left-6 flex items-center gap-3 rounded-2xl bg-white px-5 py-4 shadow-[0_20px_44px_-24px_rgba(24,40,32,0.5)]">
                <span className="grid h-11 w-11 place-items-center rounded-xl bg-ivi-mint text-ivi-greenDark">
                  <Users className="h-5 w-5" />
                </span>
                <span>
                  <span className="block font-heading text-[17px] font-extrabold text-ivi-slate">
                    Volunteers arrive weekly
                  </span>
                  <span className="block text-[13px] text-ivi-slateSoft">Ubud · Lovina · Lombok · Nusa Penida</span>
                </span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* THREE CTA CARDS */}
      <section className="bg-ivi-mint py-16">
        <div className="mx-auto grid max-w-[1400px] gap-5 px-5 md:grid-cols-3 lg:px-8">
          {GET_STARTED.map((g, i) => (
            <Reveal key={g.title} delay={i * 80}>
              <a
                href="#apply-bali"
                className="group flex h-full items-start gap-4 rounded-3xl bg-white p-7 transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5 hover:shadow-[0_26px_50px_-28px_rgba(24,40,32,0.5)]"
              >
                <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-ivi-mint text-ivi-greenDark transition-colors duration-300 group-hover:bg-ivi-green group-hover:text-white">
                  <Icon name={g.icon} className="h-5 w-5" />
                </span>
                <span>
                  <span className="block font-heading text-[18px] font-extrabold text-ivi-slate">
                    {g.title}
                  </span>
                  <span className="mt-2 block text-[14px] leading-relaxed text-ivi-slateSoft">
                    {g.text}
                  </span>
                </span>
              </a>
            </Reveal>
          ))}
        </div>
      </section>

      {/* WHY IVI + VIDEO */}
      <section className="bg-white py-24 lg:py-28">
        <div className="mx-auto grid max-w-[1400px] items-center gap-16 px-5 lg:grid-cols-2 lg:px-8">
          <div>
            <Eyebrow>Why Involvement Volunteers International?</Eyebrow>
            <h2 className="mt-5 font-heading text-[32px] font-extrabold leading-tight tracking-[-0.02em] text-ivi-slate sm:text-[42px]">
              A safe pair of hands in Indonesia
            </h2>
            <ul className="mt-8 space-y-4">
              {BALI.whyIvi.map((t, i) => (
                <Reveal key={t} delay={i * 70}>
                  <li className="flex items-start gap-3">
                    <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-ivi-green text-white">
                      <Check className="h-3.5 w-3.5" strokeWidth={3} />
                    </span>
                    <span className="text-[16px] leading-relaxed text-ivi-slate">{t}</span>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>

          <Reveal delay={100}>
            <button
              type="button"
              onClick={() => setVideo(true)}
              className="group relative block w-full overflow-hidden rounded-[28px] shadow-[0_30px_70px_-34px_rgba(24,40,32,0.55)]"
            >
              <img
                src={IMG.templeWater}
                alt="Bathing in the holy temple water in Bali"
                className="aspect-video w-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
              />
              <span className="absolute inset-0 bg-ivi-slate/35 transition-colors duration-300 group-hover:bg-ivi-slate/25" />
              <span className="absolute inset-0 grid place-items-center">
                <span className="grid h-20 w-20 place-items-center rounded-full bg-white/25 ring-1 ring-white/50 backdrop-blur-md transition-transform duration-300 group-hover:scale-110">
                  <span className="grid h-14 w-14 place-items-center rounded-full bg-ivi-green text-white">
                    <Play className="ml-0.5 h-6 w-6 fill-current" />
                  </span>
                </span>
              </span>
              <span className="absolute inset-x-6 bottom-6 text-left font-heading text-[17px] font-bold text-white drop-shadow">
                {VIDEOS.bali.title}
              </span>
            </button>
          </Reveal>
        </div>
      </section>

      {/* INTRO COPY */}
      <section className="bg-ivi-sand py-20">
        <div className="mx-auto max-w-[900px] px-5 text-center lg:px-8">
          <Eyebrow>Volunteer in Bali</Eyebrow>
          <h2 className="mt-5 font-heading text-[30px] font-extrabold leading-tight text-ivi-slate sm:text-[38px]">
            More than beeping horns and tourist beaches
          </h2>
          {BALI.intro.map((p) => (
            <p key={p.slice(0, 24)} className="mt-5 text-[17px] leading-relaxed text-ivi-slateSoft">
              {p}
            </p>
          ))}
          <p className="mt-6 text-[17px] font-semibold leading-relaxed text-ivi-slate">
            Choose between teaching &amp; kindergarten, nutrition &amp; healthcare, animal and
            environment conservation, and community construction programs.
          </p>
        </div>
      </section>

      <BaliGallery />

      {/* PROJECTS */}
      <section id="bali-projects" className="bg-white py-24 lg:py-28">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-8">
          <SectionHeading
            eyebrow="Choose your project"
            title="Programs running in Bali & Indonesia"
            text="All fees include accommodation, meals, in-country transport, airport pickup and 24/7 support."
          />

          <div className="mt-10 flex flex-wrap justify-center gap-2">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCat(c)}
                className={`rounded-full px-5 py-2.5 text-[14px] font-semibold transition-[background-color,color,border-color,transform] duration-200 hover:-translate-y-0.5 ${
                  cat === c
                    ? "bg-ivi-green text-white shadow-[0_10px_22px_-12px_rgba(90,158,47,1)]"
                    : "border border-ivi-mintDeep bg-white text-ivi-slate hover:border-ivi-green"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {shown.map((p, i) => (
              <Reveal key={p.id} delay={i * 70}>
                <div className="group flex h-full flex-col overflow-hidden rounded-3xl bg-white shadow-[0_12px_34px_-24px_rgba(24,40,32,0.45)] ring-1 ring-ivi-mintDeep transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5 hover:shadow-[0_30px_56px_-28px_rgba(24,40,32,0.5)]">
                  <div className="relative h-52 overflow-hidden">
                    <img
                      src={p.img}
                      alt={p.name}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-110"
                    />
                    {p.popular && (
                      <span className="absolute left-4 top-4 rounded-full bg-ivi-lime px-3 py-1 font-heading text-[11px] font-extrabold uppercase tracking-[0.14em] text-ivi-slate">
                        Most popular
                      </span>
                    )}
                  </div>
                  <div className="flex flex-1 flex-col p-7">
                    <p className="flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-[0.14em] text-ivi-slateSoft">
                      <MapPin className="h-3.5 w-3.5 text-ivi-green" /> {p.place}
                    </p>
                    <h3 className="mt-2 font-heading text-[20px] font-bold leading-snug text-ivi-slate">
                      {p.name}
                    </h3>
                    <p className="mt-3 flex-1 text-[15px] leading-relaxed text-ivi-slateSoft">
                      {p.blurb}
                    </p>
                    <div className="mt-6 flex items-center justify-between border-t border-ivi-mint pt-5">
                      <span>
                        <span className="block font-heading text-[22px] font-extrabold text-ivi-greenDark">
                          ${p.price}
                        </span>
                        <span className="block text-[12px] text-ivi-slateSoft">USD p/person</span>
                      </span>
                      <span className="inline-flex items-center gap-2 rounded-full bg-ivi-mint px-4 py-2 font-heading text-[13px] font-bold text-ivi-greenDark transition-colors duration-200 group-hover:bg-ivi-green group-hover:text-white">
                        Details <ArrowRight className="h-3.5 w-3.5" />
                      </span>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <BaliFaqs />
      <BaliTours />

      {/* FINAL CTA */}
      <section id="apply-bali" className="relative overflow-hidden bg-ivi-mint py-24 lg:py-28">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-8">
          <div className="grid items-center gap-10 rounded-[32px] bg-white p-9 shadow-[0_28px_64px_-42px_rgba(24,40,32,0.5)] lg:grid-cols-[1.1fr_1fr] lg:p-14">
            <div>
              <Eyebrow>Ready when you are</Eyebrow>
              <h2 className="mt-5 font-heading text-[32px] font-extrabold leading-tight text-ivi-slate sm:text-[42px]">
                Ready to volunteer in Bali?
              </h2>
              <p className="mt-5 text-[17px] leading-relaxed text-ivi-slateSoft">
                Apply online in about 5 minutes, or book a free 33-minute chat with our team and
                we&apos;ll walk you through every project, cost and arrival date.
              </p>
              <div className="mt-9 flex flex-wrap gap-4">
                <a
                  href="#apply-bali"
                  className="group inline-flex items-center gap-2 rounded-full bg-ivi-green px-7 py-4 font-heading text-[15px] font-bold text-white transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-ivi-greenDark"
                >
                  <FileText className="h-4 w-4" /> Apply now
                </a>
                <a
                  href="#apply-bali"
                  className="inline-flex items-center gap-2 rounded-full border border-ivi-slate/15 px-7 py-4 font-heading text-[15px] font-bold text-ivi-slate transition-[background-color,border-color,transform] duration-200 hover:-translate-y-0.5 hover:border-ivi-green hover:bg-ivi-mint"
                >
                  <CalendarClock className="h-4 w-4" /> Schedule a call
                </a>
                <a
                  href="#apply-bali"
                  className="inline-flex items-center gap-2 rounded-full border border-ivi-slate/15 px-7 py-4 font-heading text-[15px] font-bold text-ivi-slate transition-[background-color,border-color,transform] duration-200 hover:-translate-y-0.5 hover:border-ivi-green hover:bg-ivi-mint"
                >
                  <MessageCircle className="h-4 w-4" /> Ask a question
                </a>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img
                src={IMG.turtleGroup}
                alt="Turtle conservation group in Bali"
                className="h-56 w-full rounded-3xl object-cover"
              />
              <img
                src={IMG.ricePaddiesWalk}
                alt="Walking through the rice paddies"
                className="mt-8 h-56 w-full rounded-3xl object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <VideoModal
        open={video}
        onOpenChange={setVideo}
        videoId={VIDEOS.bali.id}
        title={VIDEOS.bali.title}
      />
    </div>
  );
}
